import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectorRef, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { Fir8865ScheduleOService } from './fir-8865-schedule-o.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  extractObject,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen
} from '../../../shared/workspace-element-context';
import * as _ from 'lodash-es';
import { parseAsLocalDate } from '../../../shared/_utils';

@Component({
    selector: 'gtw-fir-8865-schedule-o',
    templateUrl: './fir-8865-schedule-o.component.html',
    styleUrls: ['./fir-8865-schedule-o.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class Fir8865ScheduleOComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private subscriptions = new Subscription();

  // STANDARD COMPONENT PROPERTIES
  currentTabName = "8865 Schedule O";
  minimise = false;
  isSaveClicked = false;

  // Modal properties
  modalTitle = "Schedule O";
  
  // Entity data properties
  entityList: any[] = [];
  
  // Tab management
  tabs: any[] = [
    { id: 0, name: '8865 Schedule O' },
    { id: 1, name: 'Add new Reportable Transfer' },
    { id: 2, name: 'Add new Reportable Disposition' },
    { id: 3, name: 'Add new Supplemental Information' }
  ];
  activeTab = 0;
  selectedTabName = '8865 Schedule O';

  // Data arrays for Tab 1
  transferObj: any[] = [];
  dispositionObj: any[] = [];
  supplementalObj: any[] = [];

  isReportableTransferDataPresent = false;
  isReportableDispositionDataPresent = false;
  isSupplementalInformationPresent = false;

  // Collapsible sections
  reportableTransferCollapsed = true;
  requestDispositionCollapsed = true;
  supplementalInformationCollapsed = true;

  // Loading states
  showFormLoader = false;
  crudLoading = false;

  // Data from AngularJS
  rowData: any = {};
  filterParams: any = {};
  globalService: any = {};
  GENERAL_CONFIG: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};

  private isInitialized = false;

  // Component-specific data
  scheduleODetails: any[] = [];
  countries: any[] = [];
  states: any[] = [];
  typeOfProperty: any[] = [];
  partnerDetails: any[] = [];
  allocationMethod: any[] = [];
  gainRecognition: any[] = [];
  mannerOfDisposition: any[] = [];

  // Edit flag
  editFlag = false;

  // Tab 2 - Reportable Transfer form data
  selectedPartOneData: any = {};
  selectedPartOneDataCloned: any = {};
  
  // Tab 2 - Validation flags
  partnerDetailsFlag = false;
  typeOfPropertyFlag = false;
  date_flag_transfer = false;
  date_range_transfer = false;
  numberOfItemTransferredFlag = false;
  secAllocationMethodFlag = false;
  gainRecognitionFlag = false;
  percentageInterestFlag = false;
  
  // Tab 3 - Reportable Disposition form data
  selectedPartTwoData: any = {};
  selectedPartTwoDataCloned: any = {};
  
  // Tab 3 - Validation flags
  partnerDetailsFlag1 = false;
  typeOfPropertyFlag1 = false;
  date_flag_disposition = false;
  date_range_disposition = false;
  
  // Tab 4 - Supplemental Information form data
  selectedSuppleInfoData: any = {};
  selectedSuppleInfoDataCloned: any = {};
  
  // Tab 4 - Validation flags
  partnerDetailsFlag2 = false;
  
  // Attributes for save operations
  reportableTransferAttrib: any[] = [];
  reportableDispositionAttrib: any[] = [];
  supplementalInformationAttrib: any[] = [];

  constructor(
    private scheduleOService: Fir8865ScheduleOService,
    private gtwSnackbarService: GtwSnackbarService,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) {}

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const shell = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    const hydrated = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || {};
    let fp = normalizeFilterParams(activeScreen);
    if (!Object.keys(fp).length) {
      fp = extractObject(hydrated);
    }
    this.filterParams = fp;
    const mergedGp = { ...extractObject(shell.globalParams), ...extractObject(hydrated) };
    // Legacy `header-data` used `header.globalParams` as the flat object passed to saves (`_.merge({}, globalService, filterParams)`).
    this.globalService = mergedGp;
    this.USER_SETTINGS = shell.userSettingsData || {};
    this.SERVER_CONFIG = shell.serverConfigData || {};
    const gc = generalConfigFromGlobal(this.gtwGlobal);
    const mergedCfg = { ...gc, ...extractObject(shell.generalConfigData) };
    this.GENERAL_CONFIG = {
      ...mergedCfg,
      base_url: mergedCfg.base_url || mergedCfg.api || ''
    };
    const rawRow = rowPayloadFromActiveScreen(activeScreen);
    const rowObj = Array.isArray(rawRow)
      ? extractObject(rawRow[0]?.data ?? rawRow[0])
      : extractObject(rawRow);
    this.rowData = rowObj;
  }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.resetAndInit();
    this.subscriptions.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.applyWorkspaceContext();
        this.resetAndInit();
      })
    );
    this.subscriptions.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.applyWorkspaceContext();
        this.resetAndInit();
      })
    );
  }

  private resetAndInit(): void {
    this.isInitialized = false;
    this.init();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  init() {
    try {
      if (this.isInitialized) {
        return;
      }
      if (this.rowData && Object.keys(this.rowData).length > 0) {
        this.isInitialized = true;
        this.loadLookupData();
        this.loadDetails();
        this.loadAttributeNames();
      } else {
        console.warn('rowData is empty or undefined');
      }
    } catch (e) {
      console.error('Error in Fir8865ScheduleO init:', e);
    }
  }

  loadLookupData() {
    // Load countries
    this.getCountriesFIR();
    
    // Load other lookup data
    this.getTypeOfProperty();
    this.getPartnerDetails();
    this.getAllocationMethod();
    this.getGainRecognition();
    this.getMannerOfDisposition();

    if (typeof this.gtwGlobal?.getStates === 'function') {
      this.states = this.gtwGlobal.getStates();
    }
  }

  getCountriesFIR() {
    // Use ALL globalService params, not just selected ones
    const params = {
      ...this.globalService,
      tax_year: this.globalService.tax_year,
      jcd_key: this.globalService.jcd_key || 250
    };

    console.log('Loading countries with ALL params:', params);

    this.scheduleOService.getCountriesFIR(
      this.GENERAL_CONFIG.base_url,
      params,
      this.globalService
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        console.log('Countries response:', data);
        if (data.callSuccess === "1") {
          this.countries = data.jsonObject;
          console.log('Countries loaded:', this.countries);
        }
      },
      (error: any) => {
        console.error('Error loading countries:', error);
      }
    );
  }

  getTypeOfProperty() {
    // Use ALL globalService params
    const params = {
      ...this.globalService,
      tax_year: this.globalService.tax_year,
      jcd_key: this.globalService.jcd_key || 250
    };

    console.log('Loading type of property with ALL params:', params);
    
    this.scheduleOService.getTypeOfProperty(
      this.GENERAL_CONFIG.base_url,
      params,
      this.globalService
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        console.log('Type of property response:', data);
        if (data.callSuccess === "1") {
          this.typeOfProperty = data.jsonObject;
          console.log('Type of property loaded:', this.typeOfProperty);
        }
      },
      (error: any) => {
        console.error('Error loading type of property:', error);
      }
    );
  }

  getPartnerDetails() {
    // Use ALL globalService params
    const params = {
      ...this.globalService,
      tax_year: this.globalService.tax_year,
      jcd_key: this.globalService.jcd_key || 250
    };
    
    this.scheduleOService.getPartnerDetails(
      this.GENERAL_CONFIG.base_url,
      params,
      this.globalService
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        if (data.callSuccess === "1") {
          const allPartners = data.jsonObject;
          
          // Try filtering with COMBINATION_KEY
          let filtered = _.filter(allPartners, { COMBINATION_KEY: this.rowData.COMBINATION_KEY });
          
          // Try filtering with HO_COMBINATION_KEY
          let filtered2 = _.filter(allPartners, { COMBINATION_KEY: this.rowData.HO_COMBINATION_KEY });
          
          // Use the one that works
          if (filtered.length > 0) {
            this.partnerDetails = filtered;
          } else if (filtered2.length > 0) {
            this.partnerDetails = filtered2;
          } else {
            this.partnerDetails = allPartners;
          }
        }
      },
      (error: any) => {
        console.error('Error loading partner details:', error);
      }
    );
  }

  getAllocationMethod() {
    // Use ALL globalService params
    const params = {
      ...this.globalService,
      tax_year: this.globalService.tax_year,
      jcd_key: this.globalService.jcd_key || 250
    };

    console.log('Loading allocation method with ALL params:', params);
    
    this.scheduleOService.getAllocationMethod(
      this.GENERAL_CONFIG.base_url,
      params,
      this.globalService
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        console.log('Allocation method response:', data);
        if (data.callSuccess === "1") {
          this.allocationMethod = data.jsonObject;
          console.log('Allocation method loaded:', this.allocationMethod);
        }
      },
      (error: any) => {
        console.error('Error loading allocation method:', error);
      }
    );
  }

  getGainRecognition() {
    // Use ALL globalService params
    const params = {
      ...this.globalService,
      tax_year: this.globalService.tax_year,
      jcd_key: this.globalService.jcd_key || 250
    };

    console.log('Loading gain recognition with ALL params:', params);
    
    this.scheduleOService.getGainRecognition(
      this.GENERAL_CONFIG.base_url,
      params,
      this.globalService
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        console.log('Gain recognition response:', data);
        if (data.callSuccess === "1") {
          this.gainRecognition = data.jsonObject;
          console.log('Gain recognition loaded:', this.gainRecognition);
        }
      },
      (error: any) => {
        console.error('Error loading gain recognition:', error);
      }
    );
  }

  getMannerOfDisposition() {
    // Use ALL globalService params
    const params = {
      ...this.globalService,
      tax_year: this.globalService.tax_year,
      jcd_key: this.globalService.jcd_key || 250
    };

    console.log('Loading manner of disposition with ALL params:', params);

    this.scheduleOService.getMannerOfDisposition(
      this.GENERAL_CONFIG.base_url,
      params,
      this.globalService
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        console.log('Manner of disposition response:', data);
        if (data.callSuccess === "1") {
          this.mannerOfDisposition = data.jsonObject;
          console.log('Manner of disposition loaded:', this.mannerOfDisposition);
        }
      },
      (error: any) => {
        console.error('Error loading manner of disposition:', error);
      }
    );
  }

  loadDetails() {
    this.showFormLoader = true;
    
    // Use ALL globalService params
    const params = {
      ...this.globalService,
      combination_key: this.rowData.HO_COMBINATION_KEY,
      irs_form_no: 'F8865',
      jcd_key: this.globalService.jcd_key || 250
    };

    this.scheduleOService.loadScheduleODetails(
      this.GENERAL_CONFIG.base_url,
      params,
      this.globalService
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        this.showFormLoader = false;
        
        if (data.callSuccess === "1") {
          this.scheduleODetails = data.jsonObject;
          this.processScheduleOData();
          this.cdr.detectChanges();
        } else {
          this.gtwSnackbarService.openSnackBar(
            data.errorMessage || 'Error loading schedule O details',
            'gtw-snackbar--red'
          );
        }
      },
      (error: any) => {
        this.showFormLoader = false;
        console.error('Error loading schedule O details:', error);
        this.gtwSnackbarService.openSnackBar(
          'Error occurred while loading data. Please try again.',
          'gtw-snackbar--red'
        );
      }
    );
  }

  processScheduleOData() {
    // Process the raw data similar to AngularJS controller
    for (let i = 1; i < this.scheduleODetails.length; i++) {
      const tempString = this.scheduleODetails[i].object_id;
      const tempValue = this.scheduleODetails[i].attrib_value;

      this.scheduleODetails[i] = {
        i: this.scheduleODetails[i],
        tax_year: tempString.split('~')[0],
        trans_type_key: tempString.split('~')[1],
        attrib_name: tempValue.split('~')[0],
        attrib_desc: tempValue.split('~')[3],
        line_attrib_key: tempValue.split('~')[1],
        trans_details_key: tempString.split('~')[3],
        occurrence: tempString.split('~')[4],
        VALUE: this.scheduleODetails[i].VALUE,
        form_key: tempString.split('~')[6],
        line_no: this.scheduleODetails[i].line_no
      };
    }

    // Process transfer data
    this.processTransferData();
    
    // Process disposition data
    this.processDispositionData();
    
    // Process supplemental data
    this.processSupplementalData();
  }

  processTransferData() {
    this.transferObj = [];
    
    const transferArray = _.filter(this.scheduleODetails, (o: any) => {
      return o.line_no === 'rept_trans';
    });

    if (transferArray.length > 0) {
      _.uniq(_.map(transferArray, 'occurrence')).forEach((p_occurrence: any) => {
        const occList = _.filter(transferArray, { occurrence: p_occurrence });
        
        if (p_occurrence === '0') {
          return;
        }
        
        const temp: any = {};
        occList.forEach((currentObj: any) => {
          if (!currentObj['trans_details_key'] || currentObj['trans_details_key'] === '0') {
            return;
          }
          
          temp["LINE_NO"] = 'rept_trans';
          temp["occurrence"] = p_occurrence;
          temp["trans_details_key"] = currentObj['trans_details_key'];
          
          let attrName = null;
          if (currentObj['attrib_name'].indexOf("TRANSFER_DT") >= 0) {
            attrName = "DATE_OF_TRANSFER";
          } else if (currentObj['attrib_name'].indexOf("PROPERTY_DESC") >= 0) {
            attrName = "TYPE_OF_PROPERTY";
          } else if (currentObj['attrib_name'].indexOf("FAIR_MARKET_VALUE") >= 0) {
            attrName = "FAIR_MARKET_VALUE";
          } else if (currentObj['attrib_name'].indexOf("COST_BASIS_AMT") >= 0) {
            attrName = "COST_OR_OTHER_BASIS";
          } else if (currentObj['attrib_name'].indexOf("ALLOCATION_METHOD") >= 0) {
            attrName = "ALLOCATION_METHOD";
          } else if (currentObj['attrib_name'].indexOf("GAIN_TRANSFER_AMT") >= 0) {
            attrName = "GAIN_RECOGNIZED_ON_TRANSFER";
          } else {
            attrName = currentObj['attrib_name'];
          }
          
          temp[attrName] = currentObj['VALUE'];
        });
        
        if (temp.LINE_NO) {
          this.transferObj.push(temp);
        }
      });
    }

    this.isReportableTransferDataPresent = this.transferObj.length > 0;
  }

  processDispositionData() {
    this.dispositionObj = [];
    
    const dispositionArray = _.filter(this.scheduleODetails, (o: any) => {
      return o.line_no === 'rept_disp';
    });

    if (dispositionArray.length > 0) {
      _.uniq(_.map(dispositionArray, 'occurrence')).forEach((p_occurrence: any) => {
        if (p_occurrence === '0') {
          return;
        }
        
        const occList = _.filter(dispositionArray, { occurrence: p_occurrence });
        const temp: any = {};
        
        occList.forEach((currentObj: any) => {
          if (!currentObj['trans_details_key'] || currentObj['trans_details_key'] === '0') {
            return;
          }
          
          temp["LINE_NO"] = 'rept_disp';
          temp["occurrence"] = p_occurrence;
          temp["trans_details_key"] = currentObj['trans_details_key'];
          temp[currentObj['attrib_name']] = currentObj['VALUE'];
        });
        
        this.dispositionObj.push(temp);
      });
    }

    this.isReportableDispositionDataPresent = this.dispositionObj.length > 0;
  }

  processSupplementalData() {
    this.supplementalObj = [];
    
    const supplementalArray = _.filter(this.scheduleODetails, (o: any) => {
      return o.line_no === 'supple_info';
    });

    if (supplementalArray.length > 0) {
      _.uniq(_.map(supplementalArray, 'occurrence')).forEach((p_occurrence: any) => {
        if (p_occurrence === '0') {
          return;
        }
        
        const occList = _.filter(supplementalArray, { occurrence: p_occurrence });
        const temp: any = {};
        
        occList.forEach((currentObj: any) => {
          if (!currentObj['trans_details_key'] || currentObj['trans_details_key'] === '0') {
            return;
          }
          
          temp["LINE_NO"] = 'supple_info';
          temp["occurrence"] = p_occurrence;
          temp["trans_details_key"] = currentObj['trans_details_key'];
          temp[currentObj['attrib_name']] = currentObj['VALUE'];
        });
        
        if (Object.keys(temp).length > 3) { // Only push if temp has data beyond LINE_NO, occurrence, trans_details_key
          this.supplementalObj.push(temp);
        }
      });
    }

    // Check using lowercase 'line_no' from API response
    this.isSupplementalInformationPresent = supplementalArray.filter((entry: any) => 
      entry.line_no === 'supple_info' && entry.VALUE
    ).length > 0;
  }

  onTabChange(tabId: number) {
    // Clear forms when switching tabs without saving
    if (this.activeTab !== tabId && !this.editFlag) {
      if (this.activeTab === 1) {
        this.selectedPartOneData = {
          PARTNER_DETAILS: undefined,
          TYPE_OF_PROPERTY: undefined,
          DATE_OF_TRANSFER: '',
          NUMBER_OF_ITEMS_TRANSFERRED: null,
          FAIR_MARKET_VALUE: null,
          COST_OR_OTHER_BASIS: null,
          ALLOCATION_METHOD: undefined,
          GAIN_RECOGNIZED_ON_TRANSFER: null,
          PERCENTAGE_INTEREST: '',
          SUBJECT_TO_GAIN_RECOGNITION: undefined
        };
      } else if (this.activeTab === 2) {
        this.selectedPartTwoData = {
          PARTNER_DETAILS: undefined,
          TYPE_OF_PROPERTY: undefined,
          DATE_OF_ORIGINAL_TRANSFER: '',
          DATE_OF_DISPOSITION: '',
          MANNER_OF_DISPOSITION: undefined,
          GAIN_RECOGNIZED_BY_PARTNERSHIP: '',
          DEPRECIATION_RECAPTURE_RECOGNIZED: '',
          GAIN_ALLOCATED_TO_PARTNER: '',
          DEPRECIATION_RECAPTURE_ALLOCATED: ''
        };
      } else if (this.activeTab === 3) {
        this.selectedSuppleInfoData = {
          PARTNER_DETAILS: undefined,
          SUPPLEMENTAL_INFORMATION: ''
        };
      }
    }
    
    this.activeTab = tabId;
    this.selectedTabName = this.tabs.find(t => t.id === tabId)?.name || '';
    this.currentTabName = this.selectedTabName;
  }

  edit8865ScheduleO(item: any) {
    this.editFlag = true;
    
    // Switch to appropriate tab based on LINE_NO
    if (item.LINE_NO === 'rept_trans') {
      this.activeTab = 1;
      this.selectedTabName = 'Add new Reportable Transfer';
      this.currentTabName = 'Add new Reportable Transfer';
      
      // Load data for editing - start with full form structure, then populate from item
      this.selectedPartOneData = {
        LINE_NO: item.LINE_NO,
        occurrence: item.occurrence,
        trans_details_key: item.trans_details_key,
        PARTNER_DETAILS: item.PARTNER_DETAILS,
        TYPE_OF_PROPERTY: item.TYPE_OF_PROPERTY,
        DATE_OF_TRANSFER: item.DATE_OF_TRANSFER,
        NUMBER_OF_ITEMS_TRANSFERRED: item.NUMBER_OF_ITEMS_TRANSFERRED,
        FAIR_MARKET_VALUE: item.FAIR_MARKET_VALUE,
        COST_OR_OTHER_BASIS: item.COST_OR_OTHER_BASIS,
        ALLOCATION_METHOD: item.ALLOCATION_METHOD,
        GAIN_RECOGNIZED_ON_TRANSFER: item.GAIN_RECOGNIZED_ON_TRANSFER,
        PERCENTAGE_INTEREST: item.PERCENTAGE_INTEREST,
        SUBJECT_TO_GAIN_RECOGNITION: item.SUBJECT_TO_GAIN_RECOGNITION
      };
      
      // Map Partner Details
      if (item.PARTNER_DETAILS) {
        this.selectedPartOneData.PARTNER_DETAILS = this.partnerDetails.find(
          (p: any) => p.PARTNER_CODE === item.PARTNER_DETAILS
        );
      }
      
      // Map Type of Property
      if (item.TYPE_OF_PROPERTY) {
        this.selectedPartOneData.TYPE_OF_PROPERTY = this.typeOfProperty.find(
          (p: any) => p.PROP_DESC === item.TYPE_OF_PROPERTY
        );
      }
      
      // Map Allocation Method
      if (item.ALLOCATION_METHOD) {
        this.selectedPartOneData.ALLOCATION_METHOD = this.allocationMethod.find(
          (p: any) => p.PROP_TYPE === item.ALLOCATION_METHOD
        );
      }
      
      // Map Gain Recognition
      if (item.SUBJECT_TO_GAIN_RECOGNITION) {
        this.selectedPartOneData.SUBJECT_TO_GAIN_RECOGNITION = this.gainRecognition.find(
          (p: any) => p.PROP_TYPE === item.SUBJECT_TO_GAIN_RECOGNITION
        );
      }
      
      // Convert date (same as AngularJS - use local timezone, not UTC)
      if (item.DATE_OF_TRANSFER) {
        const dateSet = new Date(item.DATE_OF_TRANSFER);
        const year = dateSet.getFullYear();
        const month = (dateSet.getMonth() + 1).toString().padStart(2, '0');
        const day = dateSet.getDate().toString().padStart(2, '0');
        this.selectedPartOneData.DATE_OF_TRANSFER = `${year}-${month}-${day}`;
      }
      this.selectedPartOneDataCloned = _.cloneDeep(this.selectedPartOneData);
      
    } else if (item.LINE_NO === 'rept_disp') {
      this.activeTab = 2;
      this.selectedTabName = 'Add new Reportable Disposition';
      this.currentTabName = 'Add new Reportable Disposition';
      
      this.selectedPartTwoData = {
        LINE_NO: item.LINE_NO,
        occurrence: item.occurrence,
        trans_details_key: item.trans_details_key,
        PARTNER_DETAILS: item.PARTNER_DETAILS,
        TYPE_OF_PROPERTY: item.TYPE_OF_PROPERTY,
        DATE_OF_ORIGINAL_TRANSFER: item.DATE_OF_ORIGINAL_TRANSFER,
        DATE_OF_DISPOSITION: item.DATE_OF_DISPOSITION,
        MANNER_OF_DISPOSITION: item.MANNER_OF_DISPOSITION,
        GAIN_RECOGNIZED_BY_PARTNERSHIP: item.GAIN_RECOGNIZED_BY_PARTNERSHIP,
        DEPRECIATION_RECAPTURE_RECOGNIZED: item.DEPRECIATION_RECAPTURE_RECOGNIZED,
        GAIN_ALLOCATED_TO_PARTNER: item.GAIN_ALLOCATED_TO_PARTNER,
        DEPRECIATION_RECAPTURE_ALLOCATED: item.DEPRECIATION_RECAPTURE_ALLOCATED
      };
      
      // Map Partner Details
      if (item.PARTNER_DETAILS) {
        this.selectedPartTwoData.PARTNER_DETAILS = this.partnerDetails.find(
          (p: any) => p.PARTNER_CODE === item.PARTNER_DETAILS
        );
      }
      
      // Map Type of Property
      if (item.TYPE_OF_PROPERTY) {
        this.selectedPartTwoData.TYPE_OF_PROPERTY = this.typeOfProperty.find(
          (p: any) => p.PROP_DESC === item.TYPE_OF_PROPERTY
        );
      }
      
      // Map Manner of Disposition
      if (item.MANNER_OF_DISPOSITION) {
        this.selectedPartTwoData.MANNER_OF_DISPOSITION = this.mannerOfDisposition.find(
          (p: any) => p.PROP_TYPE === item.MANNER_OF_DISPOSITION
        );
      }
      
      // Convert dates (same as AngularJS - use local timezone, not UTC)
      if (item.DATE_OF_ORIGINAL_TRANSFER) {
        const dateSet = new Date(item.DATE_OF_ORIGINAL_TRANSFER);
        const year = dateSet.getFullYear();
        const month = (dateSet.getMonth() + 1).toString().padStart(2, '0');
        const day = dateSet.getDate().toString().padStart(2, '0');
        this.selectedPartTwoData.DATE_OF_ORIGINAL_TRANSFER = `${year}-${month}-${day}`;
      }
      
      if (item.DATE_OF_DISPOSITION) {
        const dateSet = new Date(item.DATE_OF_DISPOSITION);
        const year = dateSet.getFullYear();
        const month = (dateSet.getMonth() + 1).toString().padStart(2, '0');
        const day = dateSet.getDate().toString().padStart(2, '0');
        this.selectedPartTwoData.DATE_OF_DISPOSITION = `${year}-${month}-${day}`;
      }
      
      // Clone AFTER mapping (same as AngularJS)
      this.selectedPartTwoDataCloned = _.cloneDeep(this.selectedPartTwoData);
      
    } else if (item.LINE_NO === 'supple_info') {
      this.activeTab = 3;
      this.selectedTabName = 'Add new Supplemental Information';
      this.currentTabName = 'Add new Supplemental Information';
      
      // Load data for editing - start with full form structure, then populate from item
      this.selectedSuppleInfoData = {
        LINE_NO: item.LINE_NO,
        occurrence: item.occurrence,
        trans_details_key: item.trans_details_key,
        PARTNER_DETAILS: item.PARTNER_DETAILS,
        SUPPLEMENTAL_INFORMATION: item.SUPPLEMENTAL_INFORMATION
      };
      
      // Map Partner Details
      if (item.PARTNER_DETAILS) {
        this.selectedSuppleInfoData.PARTNER_DETAILS = this.partnerDetails.find(
          (p: any) => p.PARTNER_CODE === item.PARTNER_DETAILS
        );
      }
      
      // Clone AFTER mapping (same as AngularJS)
      this.selectedSuppleInfoDataCloned = _.cloneDeep(this.selectedSuppleInfoData);
    }
  }

  deleteSchedule(item: any) {
    const message = "Schedule has been deleted";
    const json_arrOBJ: any[] = [];
    
    this.scheduleODetails.filter((e: any) => 
      e.occurrence === item.occurrence && e.i.line_no === item.LINE_NO
    ).forEach((e: any) => {
      json_arrOBJ.push([{
        occurence: e.occurrence,
        tax_year: this.globalService.tax_year,
        trans_type_key: e.trans_type_key,
        trans_dtls_key: e.trans_details_key,
        trans_status: "A",
        line_no: item.LINE_NO
      }]);
    });

    const scheduleDetails = { addrows: [], editrows: [], removerows: json_arrOBJ };
    const scheduleSettings = [{
      tax_year: this.globalService.tax_year,
      scenario: this.globalService.scenario,
      jcd_key: 250
    }];

    this.crudLoading = true;
    
    this.scheduleOService.saveReportableTransfer(
      this.GENERAL_CONFIG.base_url,
      scheduleSettings,
      scheduleDetails,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (result: any) => {
        this.crudLoading = false;
        
        if (result.errorMessage && result.errorMessage !== 'null') {
          this.gtwSnackbarService.openSnackBar(result.errorMessage, 'gtw-snackbar--red');
        } else {
          this.gtwSnackbarService.openSnackBar(message, 'gtw-snackbar--green');
          this.loadDetails();
        }
      },
      (error: any) => {
        this.crudLoading = false;
        this.gtwSnackbarService.openSnackBar('Error occurred while deleting. Please try again.', 'gtw-snackbar--red');
      }
    );
  }

  loadAttributeNames() {
    const params = {
      ...this.globalService,
      tax_year: this.globalService.tax_year,
      scenario: this.globalService.scenario,
      jcd_key: this.globalService.jcd_key || 250,
      irs_form_no: 'F8865'
    };

    this.scheduleOService.loadAttributeNames(
      this.GENERAL_CONFIG.base_url,
      params,
      this.globalService
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        if (data.callSuccess === "1") {
          this.reportableTransferAttrib = JSON.parse(data.jsonObject[0].LINE1_ATTRIB);
          this.reportableDispositionAttrib = JSON.parse(data.jsonObject[0].LINE2_ATTRIB);
          this.supplementalInformationAttrib = JSON.parse(data.jsonObject[0].LINE3_ATTRIB);
        }
      },
      (error: any) => {
        console.error('Error loading attribute names:', error);
      }
    );
  }

  // Tab switching
  clearTabData(selectedTabIndex: number) {
    this.editFlag = false;
    this.date_range_transfer = false;
    this.date_flag_transfer = false;
    this.partnerDetailsFlag = false;
    this.typeOfPropertyFlag = false;
    this.numberOfItemTransferredFlag = false;
    this.secAllocationMethodFlag = false;
    this.gainRecognitionFlag = false;
    this.percentageInterestFlag = false;

    if (selectedTabIndex === 0) {
      this.loadDetails();
    }

    if (selectedTabIndex === 1) {
      // Clear all form fields - null for number inputs, '' for text/date, undefined for dropdowns
      this.selectedPartOneData = {
        PARTNER_DETAILS: undefined,
        TYPE_OF_PROPERTY: undefined,
        DATE_OF_TRANSFER: '',
        NUMBER_OF_ITEMS_TRANSFERRED: null,
        FAIR_MARKET_VALUE: null,
        COST_OR_OTHER_BASIS: null,
        ALLOCATION_METHOD: undefined,
        GAIN_RECOGNIZED_ON_TRANSFER: null,
        PERCENTAGE_INTEREST: '',
        SUBJECT_TO_GAIN_RECOGNITION: undefined
      };
    }

    if (selectedTabIndex === 2) {
      // Clear all form fields - '' for text/date, undefined for dropdowns
      this.selectedPartTwoData = {
        PARTNER_DETAILS: undefined,
        TYPE_OF_PROPERTY: undefined,
        DATE_OF_ORIGINAL_TRANSFER: '',
        DATE_OF_DISPOSITION: '',
        MANNER_OF_DISPOSITION: undefined,
        GAIN_RECOGNIZED_BY_PARTNERSHIP: '',
        DEPRECIATION_RECAPTURE_RECOGNIZED: '',
        GAIN_ALLOCATED_TO_PARTNER: '',
        DEPRECIATION_RECAPTURE_ALLOCATED: ''
      };
      this.date_flag_disposition = false;
      this.date_range_disposition = false;
      this.partnerDetailsFlag1 = false;
      this.typeOfPropertyFlag1 = false;
    }

    if (selectedTabIndex === 3) {
      // Clear all form fields
      this.selectedSuppleInfoData = {
        PARTNER_DETAILS: undefined,
        SUPPLEMENTAL_INFORMATION: ''
      };
      this.partnerDetailsFlag2 = false;
    }
  }

  // Validation for all tabs
  validateForm(value: any, tab: number, field: string) {
    const typeOfProperty = this.selectedPartOneData.TYPE_OF_PROPERTY ? this.selectedPartOneData.TYPE_OF_PROPERTY.PROP_TYPE : null;

    switch (tab) {
      case 1:
        switch (field) {
          case "PARTNER_DETAILS":
            this.partnerDetailsFlag = !this.selectedPartOneData.PARTNER_DETAILS || this.selectedPartOneData.PARTNER_DETAILS === "";
            break;
          case "TYPE_OF_PROPERTY":
            this.typeOfPropertyFlag = !this.selectedPartOneData.TYPE_OF_PROPERTY || this.selectedPartOneData.TYPE_OF_PROPERTY === "";
            
            // Reset dependent fields when TYPE_OF_PROPERTY changes
            if (typeOfProperty === '1') {
              this.selectedPartOneData.NUMBER_OF_ITEMS_TRANSFERRED = null;
              this.selectedPartOneData.COST_OR_OTHER_BASIS = null;
              this.selectedPartOneData.GAIN_RECOGNIZED_ON_TRANSFER = null;
              this.selectedPartOneData.ALLOCATION_METHOD = null;
            }
            break;
          case "ALLOCATION_METHOD":
            if (!this.typeOfPropertyFlag && typeOfProperty !== '1') {
              this.secAllocationMethodFlag = !this.selectedPartOneData.ALLOCATION_METHOD || this.selectedPartOneData.ALLOCATION_METHOD === "";
            }
            break;
          case "NUMBER_OF_ITEMS_TRANSFERRED":
            if (!this.typeOfPropertyFlag && typeOfProperty !== '1') {
              this.numberOfItemTransferredFlag = !this.selectedPartOneData.NUMBER_OF_ITEMS_TRANSFERRED || this.selectedPartOneData.NUMBER_OF_ITEMS_TRANSFERRED === "";
            }
            break;
          case "SUBJECT_TO_GAIN_RECOGNITION":
            this.gainRecognitionFlag = !this.selectedPartOneData.SUBJECT_TO_GAIN_RECOGNITION || this.selectedPartOneData.SUBJECT_TO_GAIN_RECOGNITION === "";
            break;
        }
        break;
      
      case 2: // Tab 3 - Reportable Disposition
        switch (field) {
          case "PARTNER_DETAILS.PARTNER_CODE":
            this.partnerDetailsFlag1 = !this.selectedPartTwoData.PARTNER_DETAILS || this.selectedPartTwoData.PARTNER_DETAILS === "";
            break;
          case "TYPE_OF_PROPERTY":
            this.typeOfPropertyFlag1 = !this.selectedPartTwoData.TYPE_OF_PROPERTY || this.selectedPartTwoData.TYPE_OF_PROPERTY === "";
            break;
          case "MANNER_OF_DISPOSITION.PROP_TYPE":
            // Validation happens in checkValidationDisposition
            break;
        }
        break;
      
      case 3: // Tab 4 - Supplemental Information
        if (field === "PARTNER_DETAILS") {
          this.partnerDetailsFlag2 = !this.selectedSuppleInfoData.PARTNER_DETAILS || this.selectedSuppleInfoData.PARTNER_DETAILS === "";
        }
        break;
    }
  }

  formatDate(tab: number) {
    const start_date = parseAsLocalDate(this.rowData.TAX_YEAR_BEGIN);
    const end_date = parseAsLocalDate(this.rowData.TAX_YEAR_END);

    if (tab === 1) {
      const dateInputData = this.selectedPartOneData.DATE_OF_TRANSFER;
      this.date_flag_transfer = !dateInputData || dateInputData === "";

      if (!this.date_flag_transfer) {
        const selectedDate = parseAsLocalDate(dateInputData);
        if (selectedDate && start_date && end_date && ((selectedDate < start_date) || (selectedDate > end_date))) {
          this.date_range_transfer = true;
        } else {
          this.date_range_transfer = false;
        }
      } else {
        this.date_range_transfer = false;
      }
    } else if (tab === 2) {
      const dateDispositionData = this.selectedPartTwoData.DATE_OF_DISPOSITION;
      this.date_flag_disposition = !dateDispositionData || dateDispositionData === "";

      if (!this.date_flag_disposition) {
        const selectedDate = parseAsLocalDate(dateDispositionData);
        if (selectedDate && start_date && end_date && ((selectedDate < start_date) || (selectedDate > end_date))) {
          this.date_range_disposition = true;
        } else {
          this.date_range_disposition = false;
        }
      } else {
        this.date_range_disposition = false;
      }
    }
  }

  checkValidation() {
    this.partnerDetailsFlag = !this.selectedPartOneData.PARTNER_DETAILS || this.selectedPartOneData.PARTNER_DETAILS === "";
    this.typeOfPropertyFlag = !this.selectedPartOneData.TYPE_OF_PROPERTY || this.selectedPartOneData.TYPE_OF_PROPERTY === "";
    
    const dateInputDataCheck = this.selectedPartOneData.DATE_OF_TRANSFER;
    const start_date = parseAsLocalDate(this.rowData.TAX_YEAR_BEGIN);
    const end_date = parseAsLocalDate(this.rowData.TAX_YEAR_END);
    
    const typeOfProperty = this.selectedPartOneData.TYPE_OF_PROPERTY ? this.selectedPartOneData.TYPE_OF_PROPERTY.PROP_TYPE : null;

    if (!this.typeOfPropertyFlag && typeOfProperty !== '1') {
      this.secAllocationMethodFlag = !this.selectedPartOneData.ALLOCATION_METHOD || this.selectedPartOneData.ALLOCATION_METHOD === "";
      this.numberOfItemTransferredFlag = !this.selectedPartOneData.NUMBER_OF_ITEMS_TRANSFERRED || this.selectedPartOneData.NUMBER_OF_ITEMS_TRANSFERRED === "";
    }
    
    this.gainRecognitionFlag = !this.selectedPartOneData.SUBJECT_TO_GAIN_RECOGNITION || this.selectedPartOneData.SUBJECT_TO_GAIN_RECOGNITION === "";

    const percentageValue = parseFloat(this.selectedPartOneData.PERCENTAGE_INTEREST?.toString().replace('%', '') || '0');
    this.percentageInterestFlag = this.selectedPartOneData.PERCENTAGE_INTEREST && 
                                   (isNaN(percentageValue) || percentageValue < 0 || percentageValue > 100);

    this.date_flag_transfer = !dateInputDataCheck || dateInputDataCheck === "";

    if (!this.date_flag_transfer) {
      const selectedDate = parseAsLocalDate(dateInputDataCheck);
      if (selectedDate && start_date && end_date && (selectedDate < start_date || selectedDate > end_date)) {
        this.date_range_transfer = true;
        this.date_flag_transfer = true;
      }
    }

    return (
      this.partnerDetailsFlag || 
      this.typeOfPropertyFlag || 
      this.date_flag_transfer || 
      this.gainRecognitionFlag ||
      this.percentageInterestFlag ||
      (typeOfProperty !== "1" && (this.secAllocationMethodFlag || this.numberOfItemTransferredFlag))
    );
  }

  save() {
    this.isSaveClicked = true;
    this.crudLoading = true;

    if (this.activeTab === 1) {
      this.saveReportableTransfer();
    } else if (this.activeTab === 2) {
      this.saveReportableDisposition();
    } else if (this.activeTab === 3) {
      this.saveSupplementalInformation();
    }
  }

  saveReportableTransfer() {
    const isNotValid = this.checkValidation();

    if (isNotValid) {
      this.gtwSnackbarService.openSnackBar('Please fill out required fields.', 'gtw-snackbar--red');
      this.isSaveClicked = false;
      this.crudLoading = false;
      return;
    }

    // Reset flags
    this.date_flag_transfer = false;
    this.date_range_transfer = false;
    this.partnerDetailsFlag = false;
    this.typeOfPropertyFlag = false;
    this.numberOfItemTransferredFlag = false;
    this.secAllocationMethodFlag = false;
    this.percentageInterestFlag = false;

    const scheduleSettings = [{
      tax_year: this.globalService.tax_year,
      scenario: this.globalService.scenario,
      jcd_key: this.globalService.jcd_key || 250
    }];

    // Get transferArray for trans_type_key and form_key (same as AngularJS)
    const transferArray = _.filter(this.scheduleODetails, (o: any) => o.line_no === 'rept_trans');
    const trans_type_key = transferArray.length > 0 ? transferArray[0].trans_type_key : null;
    const currentOccurence = this.selectedPartOneData.occurrence;
    
    const validAttributes = ["PARTNER_DETAILS", "PERCENTAGE_INTEREST", "SUBJECT_TO_GAIN_RECOGNITION", "NUMBER_OF_ITEMS_TRANSFERRED"];
    const variableAttr = ["DATE_OF_TRANSFER", "TYPE_OF_PROPERTY", "FAIR_MARKET_VALUE", "COST_OR_OTHER_BASIS", "ALLOCATION_METHOD", "GAIN_RECOGNIZED_ON_TRANSFER"];
    const attributes = ["8865_O_STOCK_NOTES_", "8865_O_INVENTORY_", "8865_O_TNGBL_PROP_TRADE_", "8865_O_INTGBL_PROP_197F9_", "8865_O_OTHER_PROP_"];

    for (let i = 0; i < variableAttr.length; i++) {
      if (this.selectedPartOneData.TYPE_OF_PROPERTY && this.selectedPartOneData.TYPE_OF_PROPERTY.PROP_TYPE !== '1') {
        const str = attributes[parseInt(this.selectedPartOneData.TYPE_OF_PROPERTY.PROP_TYPE) - 2];
        let attrName = '';
        
        if (variableAttr[i] === "DATE_OF_TRANSFER") {
          attrName = str + "TRANSFER_DT";
        } else if (variableAttr[i] === "TYPE_OF_PROPERTY") {
          attrName = str + "PROPERTY_DESC";
        } else if (variableAttr[i] === "FAIR_MARKET_VALUE") {
          attrName = str + "FAIR_MARKET_VALUE";
        } else if (variableAttr[i] === "COST_OR_OTHER_BASIS") {
          attrName = str + "COST_BASIS_AMT";
        } else if (variableAttr[i] === "ALLOCATION_METHOD") {
          attrName = str + "ALLOCATION_METHOD";
        } else if (variableAttr[i] === "GAIN_RECOGNIZED_ON_TRANSFER") {
          attrName = str + "GAIN_TRANSFER_AMT";
        }
        
        validAttributes.push(attrName);
      } else {
        validAttributes.push(variableAttr[i]);
      }
    }

    const editRow: any[] = [];
    
    for (let i = 0; i < this.reportableTransferAttrib.length; i++) {
      if (validAttributes.indexOf(this.reportableTransferAttrib[i].attrib_name) >= 0) {
        let currentTransDetailsKey = null;

        if (this.editFlag) {
          for (let j = 0; j < transferArray.length; j++) {
            for (let k = 0; k < this.reportableTransferAttrib.length; k++) {
              // CRITICAL: Use == (loose equality) for occurrence to match string with number (same as AngularJS)
              if (this.reportableTransferAttrib[k].line_attrib_key == transferArray[j].line_attrib_key && 
                  transferArray[j].occurrence == currentOccurence) {
                currentTransDetailsKey = transferArray[j].trans_details_key;
                // Mark as used so it's not reused for next attribute
                transferArray[j].line_attrib_key = "";
                transferArray[j].occurrence = "";
                break;
              }
            }
            if (currentTransDetailsKey !== null) {
              break;
            }
          }
        }

        let value = null;
        const attrName = this.reportableTransferAttrib[i].attrib_name;
        
        if (attrName === 'PARTNER_DETAILS') {
          value = this.selectedPartOneData.PARTNER_DETAILS?.PARTNER_CODE;
        } else if (attrName === 'DATE_OF_TRANSFER' || attrName.indexOf('TRANSFER_DT') >= 0) {
          if (this.selectedPartOneData.DATE_OF_TRANSFER) {
            const date = new Date(this.selectedPartOneData.DATE_OF_TRANSFER);
            value = (date.getMonth() + 1).toString().padStart(2, '0') + '/' + 
                    date.getDate().toString().padStart(2, '0') + '/' + 
                    date.getFullYear();
          }
        } else if (attrName === 'TYPE_OF_PROPERTY' || attrName.indexOf('PROPERTY_DESC') >= 0) {
          value = this.selectedPartOneData.TYPE_OF_PROPERTY?.PROP_DESC;
        } else if (attrName.indexOf('FAIR_MARKET_VALUE') >= 0) {
          value = this.selectedPartOneData.FAIR_MARKET_VALUE;
        } else if (attrName === 'COST_OR_OTHER_BASIS' || attrName.indexOf('COST_BASIS_AMT') >= 0) {
          value = this.selectedPartOneData.COST_OR_OTHER_BASIS;
        } else if (attrName.indexOf('ALLOCATION_METHOD') >= 0) {
          value = this.selectedPartOneData.ALLOCATION_METHOD?.PROP_TYPE;
        } else if (attrName === 'GAIN_RECOGNIZED_ON_TRANSFER' || attrName.indexOf('GAIN_TRANSFER_AMT') >= 0) {
          value = this.selectedPartOneData.GAIN_RECOGNIZED_ON_TRANSFER;
        } else if (attrName === 'SUBJECT_TO_GAIN_RECOGNITION') {
          value = this.selectedPartOneData.SUBJECT_TO_GAIN_RECOGNITION?.PROP_TYPE;
        } else {
          value = this.selectedPartOneData[attrName];
        }

        const tempObj = [{
          tax_year: this.globalService.tax_year,
          trans_type_key: trans_type_key,
          combination_key: this.rowData.HO_COMBINATION_KEY,
          group_obj_key: this.rowData.GROUP_OBJ_KEY,
          form_key: transferArray[0]?.form_key,
          line_no: 'rept_trans',
          occurence: this.editFlag ? currentOccurence : null,
          line_attrib_key: this.reportableTransferAttrib[i].line_attrib_key,
          Attribute_Name: attrName,
          Attribute_Value: value,
          trans_dtls_key: this.editFlag ? currentTransDetailsKey : null,
          trans_status: "A"
        }];

        editRow.push(tempObj);
      }
    }

    const scheduleDetails = { addrows: [], editrows: editRow, removerows: [] };
    const message = "New Reportable Transfer saved";

    this.scheduleOService.saveReportableTransfer(
      this.GENERAL_CONFIG.base_url,
      scheduleSettings,
      scheduleDetails,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (result: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;

        // Check callSuccess (0 = error, 1 = success)
        if (result.callSuccess === "0" || (result.errorMessage && result.errorMessage !== 'null')) {
          // ERROR: Show error message and stay on current tab
          this.gtwSnackbarService.openSnackBar(result.errorMessage || 'Error occurred while saving', 'gtw-snackbar--red');
        } else if (result.callSuccess === "1") {
          // SUCCESS: Show success message, reload data, go to tab 1
          this.gtwSnackbarService.openSnackBar(message, 'gtw-snackbar--green');
          
          // Reload data for tables
          this.loadDetails();
          
          // Go to tab 1
          this.activeTab = 0;
          this.selectedTabName = '8865 Schedule O';
          this.currentTabName = '8865 Schedule O';
          
          // Clear form and edit flag
          this.selectedPartOneData = {
            PARTNER_DETAILS: undefined,
            TYPE_OF_PROPERTY: undefined,
            DATE_OF_TRANSFER: '',
            NUMBER_OF_ITEMS_TRANSFERRED: null,
            FAIR_MARKET_VALUE: null,
            COST_OR_OTHER_BASIS: null,
            ALLOCATION_METHOD: undefined,
            GAIN_RECOGNIZED_ON_TRANSFER: null,
            PERCENTAGE_INTEREST: '',
            SUBJECT_TO_GAIN_RECOGNITION: undefined
          };
          this.editFlag = false;
          
          // Emit refresh event for grid
          this.requestGridUpdate.emit('refresh');
        }
      },
      (error: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        this.gtwSnackbarService.openSnackBar('Error occurred while saving. Please try again.', 'gtw-snackbar--red');
      }
    );
  }

  checkValidationDisposition() {
    this.partnerDetailsFlag1 = !this.selectedPartTwoData.PARTNER_DETAILS || this.selectedPartTwoData.PARTNER_DETAILS === "";
    this.typeOfPropertyFlag1 = !this.selectedPartTwoData.TYPE_OF_PROPERTY || this.selectedPartTwoData.TYPE_OF_PROPERTY === "";
    
    const dateDispositionCheck = this.selectedPartTwoData.DATE_OF_DISPOSITION;
    const start_date = parseAsLocalDate(this.rowData.TAX_YEAR_BEGIN);
    const end_date = parseAsLocalDate(this.rowData.TAX_YEAR_END);
    
    this.date_flag_disposition = !dateDispositionCheck || dateDispositionCheck === "";
    
    if (!this.date_flag_disposition) {
      const selectedDate = parseAsLocalDate(dateDispositionCheck);
      if (selectedDate && start_date && end_date && (selectedDate < start_date || selectedDate > end_date)) {
        this.date_range_disposition = true;
        this.date_flag_disposition = true;
      } else {
        this.date_range_disposition = false;
      }
    } else {
      this.date_range_disposition = false;
    }

    const mannerInvalid = !this.selectedPartTwoData.MANNER_OF_DISPOSITION || 
                          this.selectedPartTwoData.MANNER_OF_DISPOSITION === "" ||
                          this.selectedPartTwoData.MANNER_OF_DISPOSITION.PROP_TYPE === '0';

    return (
      this.date_range_disposition || 
      this.date_flag_disposition ||
      this.partnerDetailsFlag1 || 
      this.typeOfPropertyFlag1 ||
      mannerInvalid
    );
  }

  saveReportableDisposition() {
    const isNotValid = this.checkValidationDisposition();

    if (isNotValid) {
      this.gtwSnackbarService.openSnackBar('Please fill out required fields. Partner Details, Date of Disposition & Manner of Disposition', 'gtw-snackbar--red');
      this.isSaveClicked = false;
      this.crudLoading = false;
      return;
    }

    // Reset flags
    this.date_flag_disposition = false;
    this.date_range_disposition = false;
    this.partnerDetailsFlag1 = false;
    this.typeOfPropertyFlag1 = false;

    const scheduleSettings = [{
      tax_year: this.globalService.tax_year,
      scenario: this.globalService.scenario,
      jcd_key: this.globalService.jcd_key || 250
    }];

    // Get dispositionArray for trans_type_key and form_key (same as AngularJS)
    const dispositionArray = _.filter(this.scheduleODetails, (o: any) => o.line_no === 'rept_disp');
    const trans_type_key = dispositionArray.length > 0 ? dispositionArray[0].trans_type_key : null;
    const currentOccurence = this.selectedPartTwoData.occurrence;
    
    const editRow: any[] = [];
    
    for (let i = 0; i < this.reportableDispositionAttrib.length; i++) {
      // CRITICAL: Tab 3 uses .find() on scheduleODetails, not loop (AngularJS lines 1354-1357)
      const currentTransDetailsKey = this.editFlag ? 
        this.scheduleODetails.find((e: any) => 
          e.line_attrib_key == this.reportableDispositionAttrib[i].line_attrib_key && 
          e.occurrence == currentOccurence
        )?.trans_details_key : null;

      let value = null;
      const attrName = this.reportableDispositionAttrib[i].attrib_name;
      
      if (attrName === 'DATE_OF_ORIGINAL_TRANSFER') {
        if (this.selectedPartTwoData.DATE_OF_ORIGINAL_TRANSFER) {
          const date = new Date(this.selectedPartTwoData.DATE_OF_ORIGINAL_TRANSFER);
          value = (date.getMonth() + 1).toString().padStart(2, '0') + '/' + 
                  date.getDate().toString().padStart(2, '0') + '/' + 
                  date.getFullYear();
        }
      } else if (attrName === 'PARTNER_DETAILS') {
        value = this.selectedPartTwoData.PARTNER_DETAILS?.PARTNER_CODE;
      } else if (attrName === 'TYPE_OF_PROPERTY') {
        value = this.selectedPartTwoData.TYPE_OF_PROPERTY?.PROP_DESC;
      } else if (attrName === 'MANNER_OF_DISPOSITION') {
        value = this.selectedPartTwoData.MANNER_OF_DISPOSITION?.PROP_TYPE;
      } else if (attrName === 'DATE_OF_DISPOSITION') {
        if (this.selectedPartTwoData.DATE_OF_DISPOSITION) {
          const date = new Date(this.selectedPartTwoData.DATE_OF_DISPOSITION);
          value = (date.getMonth() + 1).toString().padStart(2, '0') + '/' + 
                  date.getDate().toString().padStart(2, '0') + '/' + 
                  date.getFullYear();
        }
      } else {
        value = this.selectedPartTwoData[attrName];
      }

      const tempObj = [{
        tax_year: this.globalService.tax_year,
        trans_type_key: trans_type_key,
        combination_key: this.rowData.HO_COMBINATION_KEY,
        group_obj_key: this.rowData.GROUP_OBJ_KEY,
        form_key: dispositionArray[0]?.form_key,
        line_no: 'rept_disp',
        occurence: this.editFlag ? currentOccurence : null,
        line_attrib_key: this.reportableDispositionAttrib[i].line_attrib_key,
        Attribute_Name: attrName,
        Attribute_Value: value,
        trans_dtls_key: this.editFlag ? currentTransDetailsKey : null,
        trans_status: "A"
      }];

      editRow.push(tempObj);
    }

    const scheduleDetails = { addrows: [], editrows: editRow, removerows: [] };
    const message = "New Stock Disposition is saved";

    this.scheduleOService.saveReportableDisposition(
      this.GENERAL_CONFIG.base_url,
      scheduleSettings,
      scheduleDetails,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (result: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;

        if (result.callSuccess === "0" || (result.errorMessage && result.errorMessage !== 'null')) {
          this.gtwSnackbarService.openSnackBar(result.errorMessage || 'Error occurred while saving', 'gtw-snackbar--red');
        } else if (result.callSuccess === "1") {
          this.gtwSnackbarService.openSnackBar(message, 'gtw-snackbar--green');
          
          this.loadDetails();
          
          this.activeTab = 0;
          this.selectedTabName = '8865 Schedule O';
          this.currentTabName = '8865 Schedule O';
          
          this.selectedPartTwoData = {
            PARTNER_DETAILS: undefined,
            TYPE_OF_PROPERTY: undefined,
            DATE_OF_ORIGINAL_TRANSFER: '',
            DATE_OF_DISPOSITION: '',
            MANNER_OF_DISPOSITION: undefined,
            GAIN_RECOGNIZED_BY_PARTNERSHIP: '',
            DEPRECIATION_RECAPTURE_RECOGNIZED: '',
            GAIN_ALLOCATED_TO_PARTNER: '',
            DEPRECIATION_RECAPTURE_ALLOCATED: ''
          };
          this.editFlag = false;
          
          this.requestGridUpdate.emit('refresh');
        }
      },
      (error: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        this.gtwSnackbarService.openSnackBar('Error occurred while saving. Please try again.', 'gtw-snackbar--red');
      }
    );
  }

  checkValidationSupplemental() {
    this.partnerDetailsFlag2 = !this.selectedSuppleInfoData.PARTNER_DETAILS || this.selectedSuppleInfoData.PARTNER_DETAILS === "";
    return this.partnerDetailsFlag2;
  }

  saveSupplementalInformation() {
    const isNotValid = this.checkValidationSupplemental();

    if (isNotValid) {
      this.gtwSnackbarService.openSnackBar('Please fill out required field: Partner Details', 'gtw-snackbar--red');
      this.isSaveClicked = false;
      this.crudLoading = false;
      return;
    }

    // Reset flags
    this.partnerDetailsFlag2 = false;

    const scheduleSettings = [{
      tax_year: this.globalService.tax_year,
      scenario: this.globalService.scenario,
      jcd_key: this.globalService.jcd_key || 250
    }];

    const supplementalArray = _.filter(this.scheduleODetails, (o: any) => o.line_no === 'supple_info');
    const trans_type_key = supplementalArray.length > 0 ? supplementalArray[0].trans_type_key : null;

    const currentOccurence = (!this.supplementalObj.length || !this.supplementalObj[0].occurrence) ? null : this.supplementalObj[0].occurrence;
    
    const editRow: any[] = [];
    
    for (let i = 0; i < this.supplementalInformationAttrib.length; i++) {
      const currentTransDetailsKey = this.supplementalObj.length > 0 ? 
        this.scheduleODetails.find((e: any) => 
          e.line_attrib_key == this.supplementalInformationAttrib[i].line_attrib_key && 
          e.occurrence == currentOccurence
        )?.trans_details_key : null;

      let value = null;
      const attrName = this.supplementalInformationAttrib[i].attrib_name;
      
      if (attrName === 'PARTNER_DETAILS') {
        value = this.selectedSuppleInfoData.PARTNER_DETAILS?.PARTNER_CODE;
      } else {
        value = this.selectedSuppleInfoData[attrName];
      }

      const tempObj = [{
        tax_year: this.globalService.tax_year,
        trans_type_key: trans_type_key,
        combination_key: this.rowData.HO_COMBINATION_KEY,
        group_obj_key: this.rowData.GROUP_OBJ_KEY,
        form_key: supplementalArray[0]?.form_key,
        line_no: 'supple_info',
        occurence: currentOccurence,
        line_attrib_key: this.supplementalInformationAttrib[i].line_attrib_key,
        Attribute_Name: attrName,
        Attribute_Value: value,
        trans_dtls_key: currentTransDetailsKey,
        trans_status: "A"
      }];

      editRow.push(tempObj);
    }

    const scheduleDetails = { addrows: [], editrows: editRow, removerows: [] };
    const message = "Supplemental Information is saved";

    this.scheduleOService.saveSupplementalInformation(
      this.GENERAL_CONFIG.base_url,
      scheduleSettings,
      scheduleDetails,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (result: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;

        if (result.callSuccess === "0" || (result.errorMessage && result.errorMessage !== 'null')) {
          this.gtwSnackbarService.openSnackBar(result.errorMessage || 'Error occurred while saving', 'gtw-snackbar--red');
        } else if (result.callSuccess === "1") {
          this.gtwSnackbarService.openSnackBar(message, 'gtw-snackbar--green');
          
          this.loadDetails();
          
          this.activeTab = 0;
          this.selectedTabName = '8865 Schedule O';
          this.currentTabName = '8865 Schedule O';
          
          this.selectedSuppleInfoData = {
            PARTNER_DETAILS: undefined,
            SUPPLEMENTAL_INFORMATION: ''
          };
          this.editFlag = false;
          
          this.requestGridUpdate.emit('refresh');
        }
      },
      (error: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        this.gtwSnackbarService.openSnackBar('Error occurred while saving. Please try again.', 'gtw-snackbar--red');
      }
    );
  }

  reset() {
    if (this.activeTab === 1) {
      if (this.editFlag) {
        // Clone data but remap select fields to maintain object references
        this.selectedPartOneData = _.cloneDeep(this.selectedPartOneDataCloned);
        
        // Remap select fields from original arrays to maintain Angular Material Select references
        if (this.selectedPartOneDataCloned.PARTNER_DETAILS) {
          const partnerCode = typeof this.selectedPartOneDataCloned.PARTNER_DETAILS === 'object' 
            ? this.selectedPartOneDataCloned.PARTNER_DETAILS.PARTNER_CODE 
            : this.selectedPartOneDataCloned.PARTNER_DETAILS;
          this.selectedPartOneData.PARTNER_DETAILS = this.partnerDetails.find(
            (p: any) => p.PARTNER_CODE === partnerCode
          );
        }
        
        if (this.selectedPartOneDataCloned.TYPE_OF_PROPERTY) {
          const propDesc = typeof this.selectedPartOneDataCloned.TYPE_OF_PROPERTY === 'object'
            ? this.selectedPartOneDataCloned.TYPE_OF_PROPERTY.PROP_DESC
            : this.selectedPartOneDataCloned.TYPE_OF_PROPERTY;
          this.selectedPartOneData.TYPE_OF_PROPERTY = this.typeOfProperty.find(
            (p: any) => p.PROP_DESC === propDesc
          );
        }
        
        if (this.selectedPartOneDataCloned.ALLOCATION_METHOD) {
          const allocMethod = typeof this.selectedPartOneDataCloned.ALLOCATION_METHOD === 'object'
            ? this.selectedPartOneDataCloned.ALLOCATION_METHOD.PROP_TYPE
            : this.selectedPartOneDataCloned.ALLOCATION_METHOD;
          this.selectedPartOneData.ALLOCATION_METHOD = this.allocationMethod.find(
            (p: any) => p.PROP_TYPE === allocMethod
          );
        }
        
        if (this.selectedPartOneDataCloned.SUBJECT_TO_GAIN_RECOGNITION) {
          const gainRecog = typeof this.selectedPartOneDataCloned.SUBJECT_TO_GAIN_RECOGNITION === 'object'
            ? this.selectedPartOneDataCloned.SUBJECT_TO_GAIN_RECOGNITION.PROP_TYPE
            : this.selectedPartOneDataCloned.SUBJECT_TO_GAIN_RECOGNITION;
          this.selectedPartOneData.SUBJECT_TO_GAIN_RECOGNITION = this.gainRecognition.find(
            (p: any) => p.PROP_TYPE === gainRecog
          );
        }
      } else {
        this.selectedPartOneData = {};
        this.selectedPartOneData.PERCENTAGE_INTEREST = null;
      }
      this.date_flag_transfer = false;
      this.date_range_transfer = false;
      this.partnerDetailsFlag = false;
      this.typeOfPropertyFlag = false;
      this.numberOfItemTransferredFlag = false;
      this.secAllocationMethodFlag = false;
      this.gainRecognitionFlag = false;
      this.percentageInterestFlag = false;
    } else if (this.activeTab === 2) {
      if (this.editFlag) {
        // Clone data but remap select fields to maintain object references
        this.selectedPartTwoData = _.cloneDeep(this.selectedPartTwoDataCloned);
        
        // Remap select fields from original arrays to maintain Angular Material Select references
        if (this.selectedPartTwoDataCloned.PARTNER_DETAILS) {
          const partnerCode = typeof this.selectedPartTwoDataCloned.PARTNER_DETAILS === 'object'
            ? this.selectedPartTwoDataCloned.PARTNER_DETAILS.PARTNER_CODE
            : this.selectedPartTwoDataCloned.PARTNER_DETAILS;
          this.selectedPartTwoData.PARTNER_DETAILS = this.partnerDetails.find(
            (p: any) => p.PARTNER_CODE === partnerCode
          );
        }
        
        if (this.selectedPartTwoDataCloned.TYPE_OF_PROPERTY) {
          const propDesc = typeof this.selectedPartTwoDataCloned.TYPE_OF_PROPERTY === 'object'
            ? this.selectedPartTwoDataCloned.TYPE_OF_PROPERTY.PROP_DESC
            : this.selectedPartTwoDataCloned.TYPE_OF_PROPERTY;
          this.selectedPartTwoData.TYPE_OF_PROPERTY = this.typeOfProperty.find(
            (p: any) => p.PROP_DESC === propDesc
          );
        }
        
        if (this.selectedPartTwoDataCloned.MANNER_OF_DISPOSITION) {
          const mannerDisp = typeof this.selectedPartTwoDataCloned.MANNER_OF_DISPOSITION === 'object'
            ? this.selectedPartTwoDataCloned.MANNER_OF_DISPOSITION.PROP_TYPE
            : this.selectedPartTwoDataCloned.MANNER_OF_DISPOSITION;
          this.selectedPartTwoData.MANNER_OF_DISPOSITION = this.mannerOfDisposition.find(
            (p: any) => p.PROP_TYPE === mannerDisp
          );
        }
      } else {
        this.selectedPartTwoData = {};
      }
      this.date_flag_disposition = false;
      this.partnerDetailsFlag1 = false;
      this.date_range_disposition = false;
      this.typeOfPropertyFlag1 = false;
    } else if (this.activeTab === 3) {
      if (this.editFlag) {
        // Clone data but remap select fields to maintain object references
        this.selectedSuppleInfoData = _.cloneDeep(this.selectedSuppleInfoDataCloned);
        
        // Remap select fields from original arrays to maintain Angular Material Select references
        if (this.selectedSuppleInfoDataCloned.PARTNER_DETAILS) {
          const partnerCode = typeof this.selectedSuppleInfoDataCloned.PARTNER_DETAILS === 'object'
            ? this.selectedSuppleInfoDataCloned.PARTNER_DETAILS.PARTNER_CODE
            : this.selectedSuppleInfoDataCloned.PARTNER_DETAILS;
          this.selectedSuppleInfoData.PARTNER_DETAILS = this.partnerDetails.find(
            (p: any) => p.PARTNER_CODE === partnerCode
          );
        }
      } else {
        this.selectedSuppleInfoData = {};
      }
      this.partnerDetailsFlag2 = false;
    }
  }

  cancel() {
    this.requestGridUpdate.emit('cancel');
  }
}

